import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import Button from '../../components/Button';
import accountingClientsAPI from '../../api/accountingClientsAPI';

const AccountingClientsList = () => {
    const [allClients, setAllClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [taxObligationFilter, setTaxObligationFilter] = useState('all');

    const navigate = useNavigate();

    useEffect(() => {
        fetchAccountingClients();
    }, []);

    useEffect(() => {
        filterAccountingClients();
    }, [searchTerm, statusFilter, typeFilter, taxObligationFilter, allClients]);

    const fetchAccountingClients = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await accountingClientsAPI.getAll();

            // Handle both response shapes:
            // { data: [...] } or { data: { data: [...], records: [...] } }
            const clients =
                Array.isArray(response.data)
                    ? response.data
                    : Array.isArray(response.data?.data)
                    ? response.data.data
                    : response.data?.records || [];

            setAllClients(clients);
        } catch (error) {
            setError(error.message || 'Failed to load accounting clients');
            setAllClients([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClient = async (clientId) => {
        try {
            setLoading(true);
            await accountingClientsAPI.delete(clientId);
            
            setAllClients(allClients.filter(client => client._id !== clientId));
            setSuccess('Accounting client deleted successfully');
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to delete accounting client');
        } finally {
            setLoading(false);
        }
    };

    const handleArchiveClient = async (clientId) => {
        try {
            setLoading(true);
            await accountingClientsAPI.archive(clientId);
            
            setSuccess('Accounting client archived successfully');
            fetchAccountingClients(); // Refresh the list
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to archive accounting client');
        } finally {
            setLoading(false);
        }
    };

    // Check if client has specific tax obligation
    const hasTaxObligation = (client, obligationKey) => {
        if (!client.taxObligations) return false;
        return client.taxObligations[obligationKey] === true;
    };

    const filterAccountingClients = () => {
        let filtered = [...allClients];

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(client => 
                statusFilter === 'active' ? client.isActive : !client.isActive
            );
        }

        // Apply type filter
        if (typeFilter !== 'all') {
            filtered = filtered.filter(client => client.clientType === typeFilter);
        }

        // Apply tax obligation filter
        if (taxObligationFilter !== 'all') {
            filtered = filtered.filter(client => 
                hasTaxObligation(client, taxObligationFilter)
            );
        }

        // Apply search term filter
        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(client => 
                (client.name?.toLowerCase() || '').includes(lowercasedSearch) ||
                (client.clientCode?.toLowerCase() || '').includes(lowercasedSearch) ||
                (client.tin?.toLowerCase() || '').includes(lowercasedSearch) ||
                (client.contactInfo?.email?.toLowerCase() || '').includes(lowercasedSearch)
            );
        }

        setFilteredClients(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const statusBadgeClass = (isActive) => {
        return isActive ? 'bg-success-50 text-success-700' : 'bg-ink-canvas text-ink-body';
    };

    // Format tax obligation key to readable label
    const formatTaxObligationLabel = (key) => {
        const labels = {
            percentageTax: 'Percentage Tax',
            vat: 'VAT',
            withholdingTaxExpanded: 'WHT - Expanded',
            withholdingTaxCompensation: 'WHT - Compensation',
            exciseTax: 'Excise Tax',
            incomeTax: 'Income Tax'
        };
        return labels[key] || key;
    };

    // Get active tax obligations for a client
    const getActiveTaxObligations = (client) => {
        if (!client.taxObligations) return [];

        return Object.entries(client.taxObligations)
            .filter(([_, value]) => value)
            .map(([key]) => ({
                key,
                label: formatTaxObligationLabel(key)
            }));
    };

    // Tax obligation options for filter dropdown
    const taxObligationOptions = [
        { value: 'all', label: 'All Tax Obligations' },
        { value: 'percentageTax', label: 'Percentage Tax' },
        { value: 'vat', label: 'VAT' },
        { value: 'withholdingTaxExpanded', label: 'WHT - Expanded' },
        { value: 'withholdingTaxCompensation', label: 'WHT - Compensation' },
        { value: 'exciseTax', label: 'Excise Tax' },
        { value: 'incomeTax', label: 'Income Tax' }
    ];

    const columns = [
        {
            title: 'Name',
            key: 'name',
            render: (client) => (
                <div>
                    <div className="font-medium">{client.clientCode} - {client.name}</div>
                    <span className="font-mono text-gray-500 text-sm">{client?.tin}</span>
                    {client.contactInfo?.email && (
                        <div className="text-xs text-gray-500">{client.contactInfo.email}</div>
                    )}
                </div>
            )
        },
        {
            title: 'Type',
            key: 'clientType',
            render: (client) => (
                <span className="text-xs text-ink-body capitalize">
                    {client.clientType}
                </span>
            )
        },
        {
            title: 'RDO Code',
            key: 'rdoCode',
            render: (client) => client.rdoCode || '-'
        },
        {
            title: 'Tax Obligations',
            key: 'taxObligations',
            render: (client) => {
                const activeObligations = getActiveTaxObligations(client);
                
                return (
                    <div className="min-w-[200px]">
                        {activeObligations.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {activeObligations.map((obligation, index) => (
                                    <span
                                        key={index}
                                        className="inline-block px-2 py-1 text-xs rounded-md font-medium bg-primary-50 text-primary-700"
                                        title={obligation.label}
                                    >
                                        {obligation.label}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="text-ink-body text-sm">No tax obligations</span>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Status',
            key: 'isActive',
            render: (client) => (
                <span className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${statusBadgeClass(client.isActive)}`}>
                    {client.isActive ? 'Active' : 'Archived'}
                </span>
            )
        }
    ];

    return (
        <Layout pageTitle="Accounting Clients">
            <div className="bg-gray-100 min-h-screen p-6">

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                        {success}
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                        <DebouncedSearchInput
                            placeholder="Search by name, client code, TIN, or email..."
                            onSearch={handleSearch}
                            className="flex-1 mr-4 max-w-md"
                        />

                        <div className="flex space-x-2">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                                <option value="all">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="archived">Archived</option>
                            </select>

                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                                <option value="all">All Types</option>
                                <option value="corporation">Corporation</option>
                                <option value="individual">Individual</option>
                                <option value="opc">OPC</option>
                                <option value="partnership">Partnership</option>
                            </select>

                            <select
                                value={taxObligationFilter}
                                onChange={(e) => setTaxObligationFilter(e.target.value)}
                                className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                                {taxObligationOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <Button onClick={() => navigate('/client/accounting-clients/new')} variant="primary">
                        + New Client
                    </Button>
                </div>

                {taxObligationFilter !== 'all' && (
                    <div className="mb-4 inline-block bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
                        <span className="text-sm text-primary-700 font-medium">
                            Filtered by: {taxObligationOptions.find(opt => opt.value === taxObligationFilter)?.label}
                        </span>
                    </div>
                )}

                <div className="mb-2">
                    <div className="text-sm text-gray-500">
                        {!searchTerm && taxObligationFilter === 'all' && statusFilter === 'all' && typeFilter === 'all'
                            ? `${allClients.length} total clients`
                            : `Showing ${filteredClients.length} of ${allClients.length} clients`
                        }
                        {searchTerm && ` matching "${searchTerm}"`}
                        {taxObligationFilter !== 'all' && ` with ${taxObligationOptions.find(opt => opt.value === taxObligationFilter)?.label}`}
                    </div>
                </div>

                <DataTable
                    columns={columns}
                    data={filteredClients}
                    loading={loading}
                    emptyMessage="No accounting clients found"
                    onRowClick={(client) => navigate(`/client/accounting-clients/${client._id}/edit`)}
                    onEdit={(client) => navigate(`/client/accounting-clients/${client._id}/edit`)}
                    onDelete={(client) => {
                        if (window.confirm(`Are you sure you want to delete client ${client.name}?`)) {
                            handleDeleteClient(client._id);
                        }
                    }}
                    onArchive={(client) => {
                        if (window.confirm(`Are you sure you want to ${client.isActive ? 'archive' : 'activate'} client ${client.name}?`)) {
                            handleArchiveClient(client._id);
                        }
                    }}
                    disableDelete={(client) => false}
                    archiveTooltip={(client) => 
                        client.isActive ? 'Archive client' : 'Activate client'
                    }
                    showArchive={true}
                />
            </div>
        </Layout>
    );
};

export default AccountingClientsList;